import { config, getWebSocketUrl } from '@config/index';

interface WebSocketMessage {
  event: string;
  data?: any;
  id?: string;
}

interface PendingCallback {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  timeout: NodeJS.Timeout;
}

export class CloudflareSocketService {
  private socket: WebSocket | null = null;
  private listeners: { [key: string]: Function[] } = {};
  private pendingCallbacks: Map<string, PendingCallback> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = config.websocket.reconnectAttempts;
  private reconnectDelay = config.websocket.reconnectDelay;
  private reconnectDelayMax = config.websocket.reconnectDelayMax;
  private isConnecting = false;
  private shouldReconnect = true;
  
  constructor() {
    // Initialize empty
  }
  
  connect(roomCode?: string): void {
    if (this.socket?.readyState === WebSocket.OPEN || this.isConnecting) return;
    
    this.isConnecting = true;
    this.shouldReconnect = true;
    
    // For Cloudflare Workers, we need a room-specific WebSocket URL
    let wsUrl = getWebSocketUrl();
    if (roomCode) {
      wsUrl = `${wsUrl}/room/${roomCode}`;
    }
    
    this.socket = new WebSocket(wsUrl);
    this.setupEventHandlers();
  }
  
  disconnect(): void {
    this.shouldReconnect = false;
    this.socket?.close();
    this.socket = null;
    this.isConnecting = false;
    
    // Clear all pending callbacks
    this.pendingCallbacks.forEach(callback => {
      clearTimeout(callback.timeout);
      callback.reject(new Error('Connection closed'));
    });
    this.pendingCallbacks.clear();
  }
  
  private setupEventHandlers(): void {
    if (!this.socket) return;
    
    this.socket.onopen = () => {
      console.log('Connected to server');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.emit('connected');
    };
    
    this.socket.onclose = (event) => {
      console.log('Disconnected:', event.reason);
      this.isConnecting = false;
      this.emit('disconnected', event.reason);
      
      if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    };
    
    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.isConnecting = false;
      this.emit('error', error);
    };
    
    this.socket.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        
        // Handle callback responses (messages with id)
        if (message.id && this.pendingCallbacks.has(message.id)) {
          const callback = this.pendingCallbacks.get(message.id)!;
          clearTimeout(callback.timeout);
          this.pendingCallbacks.delete(message.id);
          
          if (message.event === 'response') {
            callback.resolve(message.data);
          } else if (message.event === 'error') {
            callback.reject(new Error(message.data?.error || 'Unknown error'));
          }
          return;
        }
        
        // Handle regular events
        this.handleServerEvent(message.event, message.data);
        
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };
  }
  
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.reconnectDelayMax
    );
    
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect();
      }
    }, delay);
  }
  
  private handleServerEvent(event: string, data: any): void {
    // Map server events to the expected format
    switch (event) {
      case 'room-created':
        this.emit('roomCreated', data);
        break;
      case 'room-joined':
        this.emit('room-joined', data);
        break;
      case 'player-joined':
        this.emit('playerJoined', data);
        break;
      case 'player-disconnected':
      case 'player-left':
        this.emit('playerLeft', data);
        break;
      case 'game-started':
        console.log('[SocketService] Received game-started event:', data);
        this.emit('gameStarted', data);
        break;
      case 'move-made':
        console.log('[SocketService] Received move-made event:', data);
        this.emit('moveMade', data);
        break;
      case 'game-ended':
        this.emit('gameEnded', data);
        break;
      case 'draw-offered':
        this.emit('drawOffered', data);
        break;
      case 'draw-declined':
        this.emit('drawDeclined', data);
        break;
      case 'chat-message':
        this.emit('chatMessage', data);
        break;
      case 'timer-update':
        this.emit('timerUpdate', data);
        break;
      case 'error':
        console.error('[SocketService] Backend error received:', JSON.stringify(data, null, 2));
        this.emit('error', data);
        break;
      case 'voice-signal':
        this.emit('voiceSignal', data);
        break;
      case 'voice-chat-status':
        this.emit('voiceChatStatus', data);
        break;
      case 'voice-error':
        this.emit('voiceError', data);
        break;
      case 'error-message':
        this.emit('errorMessage', data);
        break;
      default:
        console.warn('Unhandled server event:', event, data);
    }
  }
  
  // Helper method to send messages with callback support (not used with Cloudflare Workers)
  /*private _sendMessageWithCallback(event: string, data?: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        reject(new Error('Not connected'));
        return;
      }
      
      const messageId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      const message: WebSocketMessage = {
        event,
        data,
        id: messageId
      };
      
      // Set up callback handling
      const timeout = setTimeout(() => {
        this.pendingCallbacks.delete(messageId);
        reject(new Error('Request timeout'));
      }, config.websocket.timeout);
      
      this.pendingCallbacks.set(messageId, {
        resolve,
        reject,
        timeout
      });
      
      this.socket.send(JSON.stringify(message));
    });
  }*/
  
  // Helper method to send events without callback
  private sendEvent(event: string, data?: any): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send event, not connected:', event);
      return;
    }
    
    const message: WebSocketMessage = {
      event,
      data
    };
    
    this.socket.send(JSON.stringify(message));
  }
  
  // Room methods
  async createRoom(playerName: string, settings: any): Promise<any> {
    // For Cloudflare Workers, use HTTP API first
    const apiUrl = config.api.url + '/api/create-room';
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerName, gameSettings: settings })
    });
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to create room');
    }
    
    // Connect to the room-specific WebSocket
    this.connect(data.data.roomCode);
    
    // Wait for connection and join the room
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, 10000);
      
      const handleConnected = () => {
        clearTimeout(timeout);
        this.off('connected', handleConnected);
        
        // Send join-room message after WebSocket is connected
        this.sendEvent('join-room', {
          roomCode: data.data.roomCode,
          playerName: playerName
        });
        
        // Listen for room-joined response
        const handleRoomJoined = (joinData: any) => {
          this.off('room-joined', handleRoomJoined);
          
          // If there are existing players, emit player-joined for each one
          if (joinData.existingPlayers && joinData.existingPlayers.length > 0) {
            joinData.existingPlayers.forEach((player: any) => {
              console.log('[SocketService] Emitting playerJoined for existing player:', player);
              this.emit('playerJoined', { player, timestamp: Date.now() });
            });
          }
          
          // If there's already a game state, emit game-started event
          if (joinData.gameState) {
            console.log('[SocketService] Room has active game, emitting game-started');
            this.emit('gameStarted', { gameState: joinData.gameState });
          }
          
          resolve({
            roomCode: joinData.roomCode,
            playerId: joinData.playerId,
            color: joinData.color,
            gameState: joinData.gameState
          });
        };
        
        this.on('room-joined', handleRoomJoined);
      };
      
      const handleError = (error: any) => {
        clearTimeout(timeout);
        this.off('connected', handleConnected);
        this.off('error', handleError);
        reject(error);
      };
      
      this.on('connected', handleConnected);
      this.on('error', handleError);
    });
  }
  
  async joinRoom(roomCode: string, playerName: string): Promise<any> {
    // For Cloudflare Workers, connect directly to room WebSocket
    this.connect(roomCode);
    
    // Wait for connection and join the room
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, 10000);
      
      const handleConnected = () => {
        clearTimeout(timeout);
        this.off('connected', handleConnected);
        
        // Send join-room message after WebSocket is connected
        this.sendEvent('join-room', {
          roomCode: roomCode,
          playerName: playerName
        });
        
        // Listen for room-joined response
        const handleRoomJoined = (joinData: any) => {
          this.off('room-joined', handleRoomJoined);
          
          // If there are existing players, emit player-joined for each one
          if (joinData.existingPlayers && joinData.existingPlayers.length > 0) {
            joinData.existingPlayers.forEach((player: any) => {
              console.log('[SocketService] Emitting playerJoined for existing player:', player);
              this.emit('playerJoined', { player, timestamp: Date.now() });
            });
          }
          
          // If there's already a game state, emit game-started event
          if (joinData.gameState) {
            console.log('[SocketService] Room has active game, emitting game-started');
            this.emit('gameStarted', { gameState: joinData.gameState });
          }
          
          resolve({
            roomCode: joinData.roomCode,
            playerId: joinData.playerId,
            color: joinData.color,
            gameState: joinData.gameState,
            opponent: joinData.opponent
          });
        };
        
        this.on('room-joined', handleRoomJoined);
      };
      
      const handleError = (error: any) => {
        clearTimeout(timeout);
        this.off('connected', handleConnected);
        this.off('error', handleError);
        reject(error);
      };
      
      this.on('connected', handleConnected);
      this.on('error', handleError);
    });
  }
  
  leaveRoom(): void {
    this.sendEvent('leave-room');
  }
  
  // Game methods
  makeMove(move: any): void {
    // Add timestamp to the move object itself
    const moveWithTimestamp = {
      ...move,
      timestamp: Date.now(),
    };
    this.sendEvent('make-move', { move: moveWithTimestamp });
  }
  
  resign(): void {
    this.sendEvent('resign');
  }
  
  offerDraw(): void {
    this.sendEvent('offer-draw');
  }
  
  acceptDraw(): void {
    this.sendEvent('accept-draw');
  }
  
  declineDraw(): void {
    this.sendEvent('decline-draw');
  }
  
  // Chat methods
  sendChatMessage(message: string): void {
    this.sendEvent('send-message', { message });
  }
  
  // Voice chat methods
  sendVoiceSignal(signal: any): void {
    this.sendEvent('voice-signal', signal);
  }
  
  toggleVoiceChat(enabled: boolean): void {
    this.sendEvent('voice-chat-toggle', { enabled });
  }
  
  // Utility methods
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
  
  getSocketId(): string | undefined {
    // WebSocket doesn't have a built-in ID like Socket.io
    // Could generate one if needed or get from server handshake
    return undefined;
  }
  
  getReadyState(): number | undefined {
    return this.socket?.readyState;
  }
  
  // Event emitter methods
  on(event: string, callback: Function): void {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
    console.log(`[SocketService] Listener added for event: ${event}, total listeners: ${this.listeners[event].length}`);
  }
  
  off(event: string, callback: Function): void {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
  }
  
  private emit(event: string, ...args: any[]): void {
    console.log(`[SocketService] Emitting event: ${event}, listeners: ${this.listeners[event]?.length || 0}`);
    if (!this.listeners[event]) return;
    this.listeners[event].forEach(callback => callback(...args));
  }
}

// Maintain backward compatibility with the same export name
export class SocketService extends CloudflareSocketService {}

// Singleton instance
export const socketService = new SocketService();